import { createMuiTheme } from '@material-ui/core/styles';
import { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';
import {
  createGenerateClassName,
  jssPreset,
  StylesProvider as MuiStylesProvider,
  ThemeProvider as MuiThemeProvider
} from '@material-ui/styles';
import { StylesProviderProps } from '@material-ui/styles/StylesProvider';
import { create } from 'jss';
import * as jssIncreaseSpecificity from 'jss-increase-specificity';
import jssAllImportant from 'jss-plugin-all-important';
// import isolate from "jss-plugin-isolate";
import * as React from 'react';
import { connect } from 'react-redux';
// import CssBaseline from "@material-ui/core/CssBaseline"; // can not be used w jssIncreaseSpecificity

// declare module '@material-ui/core/styles/createMuiTheme' {
// interface Theme {
//   status: {
//     danger: string;
//   };
// }
// allow configuration using `createMuiTheme`
// interface ThemeOptions {
//   status?: {
//     danger?: string;
//   };
// }
//   }

const createTheme = (props?: ThemeOptions) => createMuiTheme(props);

//#region  theme provider
// AppDesignScheme to theme map
// isDark: theme.palette.type === 'dark'
// Primary: theme.palette.background.default,
// Secondary: theme.palette.primary.main,
// Tertiary: theme.palette.background.default,
// Font: theme.typography.fontFamily
interface CustomCssToThemeProps {
  primary: string;
  darkBackground: string;
  background: string;
  fontFamily: string;
  dark: boolean;
}

export function customCssToTheme(props: CustomCssToThemeProps) {
  const { primary, darkBackground, background, fontFamily, dark } = props;
  const theme = createTheme({
    palette: {
      type: dark ? 'dark' : 'light',
      primary: {
        main: primary, // mexicancafe red '#b20837',
        contrastText: '#fff' // TEMP until we remove all Radium & convert to material-ui
      },
      text: {
        // primary: dark ? background : primary
      },
      background: {
        default: dark ? darkBackground : background
      }
    },
    typography: {
      fontFamily,
      fontSize: 14,
      //#region stylesheets/_typography.scss
      h1: {
        fontSize: '36px',
        fontWeight: 400,
        lineHeight: 1.1
      },
      h2: {
        fontSize: '30px',
        fontWeight: 400,
        lineHeight: 1.1
      },
      h3: {
        fontSize: '24px',
        fontWeight: 400,
        lineHeight: 1.1
      },
      h4: {
        fontSize: '18px',
        fontWeight: 400,
        lineHeight: 1.1
      },
      h5: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: 1.1
      },
      h6: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.1
      },
      body1: {
        fontSize: '16px'
      },
      body2: {
        fontSize: '16px'
      },
      fontWeightBold: 700,
      button: {
        textTransform: 'none',
        fontWeight: 400,
        fontSize: '16px'
      },
      caption: {
        fontSize: 12,
        fontWeight: 'normal',
        fontStyle: 'normal',
        lineHeight: '1.33',
        color: '#888'
      }

      //#endregion
    },
    zIndex: {
      mobileStepper: 101000,
      appBar: 101100,
      drawer: 101200,
      modal: 101300,
      snackbar: 101400,
      tooltip: 101500
    },
    overrides: {
      MuiIcon: {
        // fa icons getting cut
        fontSizeLarge: {
          overflow: 'visible'
        }
      }
    }
  });

  // TODO make font size responsive
  // theme.typography.h2 = {
  //   ...theme.typography.h2,
  //   fontSize: "20px",
  //   fontWeight: 400,
  //   lineHeight: 1.1,
  //   [theme.breakpoints.up("md")]: {
  //     fontSize: "30px"
  //   }
  // };

  return theme;
}
export const ThemeProvider = connect<
  {
    customCss: WebOrder.State['appDesignScheme'];
    isDark: WebOrder.State['flipdish']['isDark'];
  },
  {},
  { children: React.ReactNode },
  WebOrder.State
>((state) => ({
  customCss: state.appDesignScheme,
  isDark: state.flipdish.isDark
}))(({ dispatch, customCss, isDark, children }) => {
  const {
    // not naming mistakes... that's how sht works here
    Primary: background,
    Secondary: primary,
    Tertiary: secondary,
    Font: fontFamily
  } = customCss;

  const [theme, setTheme] = React.useState(() =>
    customCssToTheme({
      primary,
      background,
      darkBackground: secondary,
      fontFamily,
      dark: isDark
    })
  );

  React.useEffect(() => {
    setTheme((theme) => {
      if (
        theme.palette.type === (isDark ? 'dark' : 'light') &&
        theme.palette.primary.main === primary
      ) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'SET_THEME:NO_CHANGE - listening for changes only on dark mode and primary color',
            theme,
            primary,
            isDark
          );
        }
        return theme;
      }

      const newTheme = customCssToTheme({
        primary,
        background,
        darkBackground: secondary,
        fontFamily,
        dark: isDark
      });
      if (process.env.NODE_ENV === 'development') {
        console.log('SET_THEME', newTheme, {
          primary,
          background,
          secondary,
          fontFamily,
          isDark
        });
      }
      return newTheme;
    });
  }, [primary, background, secondary, fontFamily, isDark]);

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
});
//#endregion

//#region style provider
const generateClassName = createGenerateClassName({
  productionPrefix: 'flipdish-web-order-c'
});
const jss = create({
  plugins: [
    // isolate({ isolate: "root", reset: "inherited" }), // not 'root' because that's used throughout MUI - see https://github.com/cssinjs/jss/issues/326
    ...jssPreset().plugins,
    jssRemToPx(),
    jssAllImportant(),
    jssIncreaseSpecificity()
  ]
});
export const StylesProvider: React.FC<StylesProviderProps> = (props) => (
  <MuiStylesProvider {...props} generateClassName={generateClassName} jss={jss} />
);
//#endregion

//#region jss-plugins
/**
 * Replace all css rules with rem to be px
 * Intended to be used when you have no control over the document
 * Takes baseFontSize: number, which is the base font size
 */
export function jssRemToPx(options = { baseFontSize: 16 }) {
  function onProcessStyle(style, rule) {
    if (rule.type !== 'style') return style;

    for (const prop in style) {
      if (style[prop] && style[prop].charAt && style[prop].slice(-3) === 'rem') {
        style[prop] = convertRemToPx(style[prop], options.baseFontSize);
      }
    }

    return style;
  }

  function onChangeValue(value, prop, rule) {
    if (value && value.charAt && value.slice(-3) === 'rem') {
      return convertRemToPx(value, options.baseFontSize);
    }

    return value;
  }

  function convertRemToPx(value, baseFontSize) {
    let newValue = value.slice(0, value.length - 3);
    newValue = newValue * baseFontSize;
    return newValue + 'px';
  }

  return {
    onProcessStyle,
    onChangeValue
  };
}

/**
 * Replace all css rules with em to be px
 * Intended to be used when you have no control over the document
 * Takes baseFontSize: number, which is the base font size
 */
export function jssEmToPx(options = { baseFontSize: 16 }) {
  function onProcessStyle(style, rule) {
    if (rule.type !== 'style') return style;

    for (const prop in style) {
      if (style[prop] && style[prop].charAt && style[prop].slice(-2) === 'em') {
        style[prop] = convertEmToPx(style[prop], options.baseFontSize);
      }
    }

    return style;
  }

  function onChangeValue(value, prop, rule) {
    if (value && value.charAt && value.slice(-2) === 'em') {
      return convertEmToPx(value, options.baseFontSize);
    }
    return value;
  }

  function convertEmToPx(value, baseFontSize) {
    let newValue = value.slice(0, value.length - 2);
    newValue = newValue * baseFontSize;
    return newValue + 'px';
  }

  return {
    onProcessStyle,
    onChangeValue
  };
}
//#endregion
